import { Component } from '@theme/component';

const STORAGE_KEY = 'cavador-flooring-project:v1';
const MAX_FINISHES = 8;
const MAX_MATERIALS = 8;
const MAX_ROOMS = 20;
const MAX_BYTES = 100000;
const SQM_TO_SQFT = 1 / (0.3048 * 0.3048);
const FIELDS = ['model', 'material', 'collection', 'colour', 'finish', 'dimensions', 'thickness', 'wear_layer', 'installation', 'water_resistance', 'underpad', 'warranty', 'coverage_note'];

const cleanText = (value, max = 160) => typeof value === 'string' ? value.trim().slice(0, max) : '';
const number = value => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 10000000 ? value : null;
const positive = value => number(value) > 0 ? value : null;
const escapeHTML = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);

/** Only same-store product links and supplier images already hosted by Shopify are retained. */
export function normalizeProduct(input, origin = window.location.origin) {
  if (!input || typeof input !== 'object' || !/^\d+$/.test(String(input.id)) || !/^[a-z0-9-]{1,100}$/.test(input.handle || '')) return null;
  const title = cleanText(input.title);
  let url;
  let image = '';
  try {
    const parsed = new URL(input.url, origin);
    if (parsed.origin !== origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9-]+\/?$/i.test(parsed.pathname)) return null;
    if (parsed.pathname.split('/').filter(Boolean).at(-1) !== input.handle) return null;
    url = parsed.pathname;
  } catch { return null; }
  if (!title) return null;
  if (input.image) {
    try {
      const parsed = new URL(input.image, origin);
      if (parsed.protocol === 'https:' && (parsed.hostname === 'cdn.shopify.com' || (parsed.origin === origin && parsed.pathname.startsWith('/cdn/shop/')))) image = parsed.href;
    } catch {}
  }
  const values = {};
  for (const field of FIELDS) values[field] = cleanText(input.values?.[field], 600);
  values.coverage = positive(input.coveragePerBox) || positive(input.values?.coverage);
  const planks = positive(input.planksPerBox);
  const price = number(input.priceCents);
  return { id: String(input.id).slice(0, 30), handle: input.handle, title, url, image, values, coveragePerBox: values.coverage, planksPerBox: Number.isInteger(planks) ? planks : null, priceCents: Number.isInteger(price) ? price : null, currency: /^[A-Z]{3}$/.test(input.currency || '') ? input.currency : '' };
}

/** Stored quantities are discarded; totals are always derived from this finish's verified coverage. */
export function normalizeCalculation(input, product) {
  if (!input || input.version !== 1 || input.handle !== product.handle || !['area', 'dims', 'rooms'].includes(input.mode) || !['imperial', 'metric'].includes(input.units) || !Array.isArray(input.rooms) || !input.rooms.length || input.rooms.length > MAX_ROOMS) return null;
  if (input.mode !== 'rooms' && (input.rooms.length !== 1 || input.rooms[0]?.mode !== input.mode)) return null;
  const waste = number(input.waste);
  if (![0, 5, 10, 15, 20].includes(waste)) return null;
  const rooms = [];
  for (const room of input.rooms) {
    if (!room || !['area', 'dims'].includes(room.mode)) return null;
    const normalized = { name: cleanText(room.name, 60), mode: room.mode, area: number(room.area), length: number(room.length), width: number(room.width) };
    rooms.push(normalized);
  }
  let areaSqft = 0;
  let measurementsComplete = true;
  for (const room of rooms) {
    const area = room.mode === 'dims' ? (room.length || 0) * (room.width || 0) : (room.area || 0);
    if (!(area > 0) || !Number.isFinite(area) || area > 100000000) measurementsComplete = false;
    else areaSqft += area * (input.units === 'metric' ? SQM_TO_SQFT : 1);
  }
  const coveragePerBox = product.values.coverage;
  const requiredSqft = areaSqft * (1 + waste / 100);
  const ratio = coveragePerBox ? requiredSqft / coveragePerBox : null;
  const valid = measurementsComplete && areaSqft > 0 && coveragePerBox > 0 && Number.isFinite(ratio) && ratio <= 10000000;
  const tolerance = ratio ? Number.EPSILON * 8 * Math.max(1, Math.abs(ratio)) : 0;
  const boxes = valid ? Math.max(1, Math.ceil(ratio - tolerance)) : null;
  return { version: 1, handle: product.handle, mode: input.mode, units: input.units, waste, rooms, areaSqft, requiredSqft, boxes, coveragePerBox, planksPerBox: product.planksPerBox, totalCoverageSqft: valid ? boxes * coveragePerBox : null, priceCents: product.priceCents, measurementsComplete, valid };
}

export function normalizeProject(input, defaultName = '', origin = window.location.origin) {
  const project = { version: 1, name: cleanText(defaultName, 80), updatedAt: '', finishes: [], materials: [], notes: '', studioLook: null };
  if (!input || input.version !== 1 || !Array.isArray(input.finishes) || input.finishes.length > MAX_FINISHES) return project;
  project.name = cleanText(input.name, 80) || project.name;
  project.notes = cleanText(input.notes, 2500);
  project.studioLook = normalizeStudioReference(input.studioLook);
  project.updatedAt = typeof input.updatedAt === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(input.updatedAt) && Number.isFinite(Date.parse(input.updatedAt)) ? input.updatedAt.slice(0, 30) : '';
  const seen = new Set();
  for (const finish of input.finishes) {
    const product = normalizeProduct(finish?.product, origin);
    if (!product || seen.has(product.handle)) continue;
    seen.add(product.handle);
    const savedAt = typeof finish.savedAt === 'string' && Number.isFinite(Date.parse(finish.savedAt)) ? finish.savedAt.slice(0, 30) : project.updatedAt;
    project.finishes.push({ product, calculation: normalizeCalculation(finish.calculation, product), savedAt });
  }
  const materialKeys = new Set();
  for (const entry of Array.isArray(input.materials) ? input.materials.slice(0, MAX_MATERIALS) : []) {
    const product = normalizeMaterial(entry?.product, origin);
    if (!product || materialKeys.has(materialKey(product))) continue;
    materialKeys.add(materialKey(product));
    const savedAt = typeof entry.savedAt === 'string' && Number.isFinite(Date.parse(entry.savedAt)) ? entry.savedAt.slice(0, 30) : project.updatedAt;
    project.materials.push({ product, calculation: normalizeMaterialCalculation(entry.calculation, product), savedAt });
  }
  return project;
}

export function normalizeMaterial(input, origin = window.location.origin) {
  if (!input || !['wall', 'tile'].includes(input.surface)) return null;
  const product = normalizeProduct({ ...input, id: input.productId || input.id, title: input.title || input.name, url: input.url || input.productUrl, image: input.image || input.imageUrl, coveragePerBox: input.coveragePerBox ?? input.coverageSqFt, planksPerBox: input.piecesPerPack }, origin);
  if (!product || !/^\d{1,30}$/.test(String(input.variantId || ''))) return null;
  const pieces = positive(input.piecesPerPack ?? input.planksPerBox);
  const variantId = String(input.variantId);
  return { ...product, surface: input.surface, variantId, variantTitle: cleanText(input.variantTitle, 120), sku: cleanText(input.sku, 80), url: `${product.url}?variant=${variantId}`, piecesPerPack: Number.isSafeInteger(pieces) ? pieces : null, panelWidthMm: positive(input.panelWidthMm), panelHeightMm: positive(input.panelHeightMm) };
}

const materialKey = product => `${product.surface}:${product.id}:${product.variantId}`;
const ceiling = value => Math.max(1, Math.ceil(value - Number.EPSILON * 8 * Math.max(1, Math.abs(value))));

export function normalizeMaterialCalculation(input, product) {
  if (!input || input.version !== 1 || input.surface !== product.surface || input.handle !== product.handle || String(input.variantId) !== product.variantId || !['area', 'dims'].includes(input.mode) || !['imperial', 'metric'].includes(input.units) || ![0, 5, 10, 15, 20].includes(input.waste)) return null;
  const calculation = { version: 1, surface: product.surface, handle: product.handle, variantId: product.variantId, mode: input.mode, units: input.units, waste: input.waste, area: number(input.area), length: number(input.length), width: number(input.width), walls: [] };
  if (product.surface === 'wall') {
    if (input.mode === 'dims' && (!Array.isArray(input.walls) || input.walls.length < 1)) return null;
    calculation.walls = (Array.isArray(input.walls) ? input.walls : []).map(wall => ({ width: number(wall?.width), height: number(wall?.height) }));
  }
  const dimensionalArea = product.surface === 'wall' ? calculation.walls.reduce((sum, wall) => sum + (wall.width || 0) * (wall.height || 0), 0) : (calculation.length || 0) * (calculation.width || 0);
  const complete = input.mode === 'area' ? calculation.area > 0 : product.surface === 'wall' ? calculation.walls.every(wall => wall.width > 0 && wall.height > 0) : calculation.length > 0 && calculation.width > 0;
  const areaSqft = (input.mode === 'area' ? calculation.area || 0 : dimensionalArea) * (input.units === 'metric' ? SQM_TO_SQFT : 1);
  const requiredSqft = areaSqft * (1 + input.waste / 100);
  const ratio = product.coveragePerBox > 0 ? requiredSqft / product.coveragePerBox : null;
  let valid = complete && areaSqft > 0 && Number.isFinite(areaSqft) && Number.isFinite(ratio) && ratio > 0 && ratio <= 10000000;
  let boxes = valid ? ceiling(ratio) : null;
  let horizontalJoint = false;
  if (valid && product.surface === 'wall' && input.mode === 'dims') {
    valid = product.panelWidthMm > 0 && product.panelHeightMm > 0 && product.piecesPerPack > 0;
    if (valid) {
      let panels = 0;
      for (const wall of calculation.walls) {
        const metres = input.units === 'metric' ? 1 : 0.3048;
        const columns = ceiling(wall.width * metres * 1000 / product.panelWidthMm);
        const rows = ceiling(wall.height * metres * 1000 / product.panelHeightMm);
        panels += columns * rows;
        horizontalJoint ||= rows > 1;
      }
      valid = Number.isSafeInteger(panels) && panels <= 10000000;
      if (valid) boxes = Math.max(boxes, ceiling(panels / product.piecesPerPack));
    }
  }
  return { ...calculation, measurementsComplete: complete, areaSqft, requiredSqft, coveragePerBox: product.coveragePerBox, boxes: valid ? boxes : null, totalCoverageSqft: valid ? boxes * product.coveragePerBox : null, horizontalJoint, valid };
}

export function normalizeStudioReference(input) {
  if (!input || typeof input !== 'object') return null;
  const lookId = typeof input.lookId === 'string' && /^[a-z0-9-]{1,80}$/i.test(input.lookId) ? input.lookId : '';
  const selection = surface => {
    const value = input[surface];
    return value && /^[a-z0-9-]{1,100}$/.test(value.handle || '') ? { handle: value.handle, variantId: /^\d{1,30}$/.test(String(value.variantId || '')) ? String(value.variantId) : '', title: cleanText(value.title || value.name, 160) } : null;
  };
  return { lookId, roomName: cleanText(input.roomName, 100), floor: selection('floor'), tile: selection('tile'), wall: selection('wall'), previewReady: Boolean(lookId && input.previewReady === true), savedAt: typeof input.savedAt === 'string' && Number.isFinite(Date.parse(input.savedAt)) ? input.savedAt.slice(0, 30) : '' };
}

export function persistProject(storage, project) {
  const serialized = JSON.stringify(project);
  if (serialized.length > MAX_BYTES) return false;
  try { storage.setItem(STORAGE_KEY, serialized); return true; } catch { return false; }
}

const formatNumber = (value, locale) => new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
const formatMoney = (value, currency, locale) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || !currency) return '';
  try { return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value / 100); } catch { return ''; }
};
const roomName = (room, index, labels) => room.name || labels.room.replace('__NUMBER__', String(index + 1));
const roomValue = (room, units, labels, locale) => room.mode === 'dims'
  ? `${room.length === null ? '—' : formatNumber(room.length, locale)} × ${room.width === null ? '—' : formatNumber(room.width, locale)} ${units === 'metric' ? labels.m : labels.ft}`
  : `${room.area === null ? '—' : formatNumber(room.area, locale)} ${units === 'metric' ? labels.sqm : labels.sqft}`;

/** All merchant and shopper content is escaped; the downloaded brief contains no executable script. */
export function buildProjectBrief(project, labels, { origin = window.location.origin, locale = 'en' } = {}) {
  const safe = normalizeProject(project, labels.default_name, origin);
  const e = escapeHTML;
  const n = value => formatNumber(value, locale);
  const definition = (label, value) => `<div><dt>${e(label)}</dt><dd>${e(value)}</dd></div>`;
  let sections = safe.finishes.map(({ product, calculation, savedAt }, index) => {
    const details = FIELDS.map(field => definition(labels[field], product.values[field] || labels.not_confirmed)).join('');
    const price = formatMoney(product.priceCents, product.currency, locale) || labels.price_pending;
    const coverage = product.values.coverage ? `${n(product.values.coverage)} ${labels.sqft}` : labels.not_confirmed;
    let estimate = `<p class="pending">${e(labels.measurements_pending)}</p>`;
    if (calculation) {
      const measured = calculation.measurementsComplete;
      const rooms = calculation.rooms.map((room, i) => `<tr><th scope="row">${e(roomName(room, i, labels))}</th><td>${e(roomValue(room, calculation.units, labels, locale))}</td></tr>`).join('');
      const totals = definition(labels.waste, `${n(calculation.waste)}%`) +
        (measured ? definition(labels.area, `${n(calculation.areaSqft)} ${labels.sqft}`) + definition(labels.required, `${n(calculation.requiredSqft)} ${labels.sqft}`) : '') +
        (calculation.valid ? definition(labels.boxes, n(calculation.boxes)) + definition(labels.total_coverage, `${n(calculation.totalCoverageSqft)} ${labels.sqft}`) + definition(labels.estimated_cost, formatMoney(product.priceCents === null ? null : calculation.boxes * product.priceCents, product.currency, locale) || labels.price_pending) : '');
      const pending = !measured ? labels.incomplete : !product.values.coverage ? labels.coverage_pending : '';
      estimate = `<h3>${e(labels.rooms)}</h3><table>${rooms}</table><dl>${totals}</dl>${pending ? `<p class="pending">${e(pending)}</p>` : ''}`;
    }
    const savedDate = savedAt ? new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'America/Toronto' }).format(new Date(savedAt)) : '';
    const referenceDate = savedDate ? `<p class="reference">${e(labels.saved_reference.replace('__DATE__', savedDate))}</p>` : '';
    return `<section class="finish"><header>${product.image ? `<img src="${e(product.image)}" alt="${e(labels.brief_supplier_image)}" width="112" height="112">` : ''}<div><p class="number">${index + 1}</p><h2>${e(product.title)}</h2><a href="${e(new URL(product.url, origin).href)}">${e(labels.brief_source)}</a>${referenceDate}</div></header><dl class="facts">${details}${definition(labels.box_coverage, coverage)}${definition(labels.planks, product.planksPerBox || labels.not_confirmed)}${definition(labels.price, price)}</dl>${estimate}</section>`;
  }).join('');
  sections += safe.materials.map(({ product, calculation }) => {
    const facts = definition(labels.surface, labels[product.surface]) + definition(labels.finish, product.variantTitle || product.title) + definition(labels.model, product.sku || labels.not_confirmed) + definition(labels.box_coverage, product.coveragePerBox ? `${n(product.coveragePerBox)} ${labels.sqft}` : labels.not_confirmed) + definition(labels.pieces, product.piecesPerPack || labels.not_confirmed) + definition(labels.price, formatMoney(product.priceCents, product.currency, locale) || labels.price_pending);
    let measurements = '';
    if (calculation?.mode === 'dims' && product.surface === 'wall') measurements = calculation.walls.map((wall, i) => definition(`${labels.wall} ${i + 1}`, `${wall.width ?? '—'} × ${wall.height ?? '—'} ${calculation.units === 'metric' ? labels.m : labels.ft}`)).join('');
    else if (calculation?.mode === 'dims') measurements = definition(labels.dimensions, `${calculation.length ?? '—'} × ${calculation.width ?? '—'} ${calculation.units === 'metric' ? labels.m : labels.ft}`);
    const totals = calculation?.valid ? definition(labels.area, `${n(calculation.areaSqft)} ${labels.sqft}`) + definition(labels.waste, `${n(calculation.waste)}%`) + definition(product.surface === 'wall' ? labels.packs : labels.boxes, n(calculation.boxes)) + definition(labels.total_coverage, `${n(calculation.totalCoverageSqft)} ${labels.sqft}`) + definition(labels.estimated_cost, formatMoney(product.priceCents === null ? null : product.priceCents * calculation.boxes, product.currency, locale) || labels.price_pending) : '';
    const pending = calculation?.valid ? '' : `<p class="pending">${e(calculation ? labels.coverage_pending : labels.measurements_pending)}</p>`;
    return `<section class="finish"><h2>${e(product.title)}${product.variantTitle && product.variantTitle !== 'Default Title' ? ` · ${e(product.variantTitle)}` : ''}</h2><a href="${e(new URL(product.url, origin).href)}">${e(labels.brief_source)}</a><dl>${facts}${measurements}${totals}</dl>${pending}${calculation?.horizontalJoint ? `<p>${e(labels.wall_joint)}</p>` : ''}${product.surface === 'wall' && calculation?.mode === 'dims' ? `<p>${e(labels.wall_layout)}</p>` : ''}</section>`;
  }).join('');
  if (safe.notes) sections += `<section class="finish"><h2>${e(labels.notes)}</h2><p style="white-space:pre-wrap">${e(safe.notes)}</p></section>`;
  if (safe.studioLook) sections += `<section class="finish"><h2>${e(labels.studio_heading)}</h2><p>${e(safe.studioLook.roomName)}</p><p>${e(studioStatus(safe.studioLook, labels))}</p><p>${e([safe.studioLook.floor?.title, safe.studioLook.tile?.title, safe.studioLook.wall?.title].filter(Boolean).join(' + '))}</p><a href="${e(studioLink(safe.studioLook, origin))}">${e(labels.open_studio)}</a><p>${e(labels.preview_notice)}</p></section>`;
  const date = safe.updatedAt ? new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'America/Toronto' }).format(new Date(safe.updatedAt)) : '';
  return `<!doctype html><html lang="${e(locale)}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="referrer" content="no-referrer"><title>${e(safe.name)} · ${e(labels.brief_title)}</title><style>body{max-width:850px;margin:40px auto;padding:0 24px;color:#202020;background:#fff;font:14px/1.6 Arial,sans-serif}h1{margin:4px 0 12px;font:normal 36px/1.2 Georgia,serif}h2{margin:2px 0 8px;font:normal 24px/1.3 Georgia,serif}h3{margin:24px 0 8px;font-size:14px}a{color:#64232e;overflow-wrap:anywhere}.eyebrow,.number{color:#676767;font-size:11px;letter-spacing:.12em;text-transform:uppercase}.note{padding:16px 0;border-block:1px solid #dedede;color:#555}.finish{margin-top:32px;padding-top:24px;border-top:1px solid #dedede}.finish header{display:flex;gap:20px;align-items:center}.finish img{object-fit:cover}.number{margin:0}dl{margin:20px 0}dl>div{display:grid;grid-template-columns:180px minmax(0,1fr);gap:12px;padding:5px 0;border-bottom:1px solid #eee}dt{color:#676767}dd{margin:0;overflow-wrap:anywhere}table{width:100%;border-collapse:collapse}th,td{text-align:left;font-weight:normal;padding:8px 0;border-bottom:1px solid #eee}td{text-align:right}.pending{color:#676767}footer{margin-top:36px;color:#676767;font-size:12px}@media(max-width:500px){body{margin:24px auto;padding:0 18px}dl>div{grid-template-columns:minmax(0,1fr) minmax(0,1.3fr)}.finish header{align-items:flex-start}.finish img{width:72px;height:72px}h1{font-size:30px}h2{font-size:21px}}@media print{body{max-width:none;margin:0;padding:0;font-size:11px}.finish{break-inside:avoid}h1{font-size:28px}h2{font-size:20px}a{color:#202020}a[href]::after{content:' (' attr(href) ')';font-size:9px}header img{width:80px;height:80px}footer{font-size:10px}}</style></head><body><p class="eyebrow">CAVADOR · ${e(labels.brief_title)}</p><h1>${e(safe.name)}</h1>${date ? `<p>${e(labels.brief_date)}: ${e(date)}</p>` : ''}<p class="note">${e(labels.installer_notice)}</p>${sections}<footer>${e(labels.brief_reference_notice)}</footer></body></html>`;
}

function studioLink(reference, origin = window.location.origin, path = '/collections/all-flooring?view=cavador-room-studio') {
  const url = new URL(path, origin);
  if (reference?.lookId) url.searchParams.set('look', reference.lookId);
  return url.href;
}

export function studioLookAvailable(reference, storage = globalThis.localStorage) {
  if (!reference?.lookId) return false;
  try {
    const raw = storage?.getItem('cavador-studio-real-looks-v1');
    if (!raw || raw.length > 6000000) return false;
    const looks = JSON.parse(raw);
    return Array.isArray(looks) && looks.slice(0, 8).some(look => look?.id === reference.lookId);
  } catch { return false; }
}

function studioStatus(reference, labels) {
  return reference.lookId && !studioLookAvailable(reference) ? labels.preview_unavailable : reference.previewReady ? labels.preview_saved : labels.palette_saved;
}

export function buildProjectText(project, labels, { origin = window.location.origin, locale = 'en' } = {}) {
  const safe = normalizeProject(project, labels.default_name, origin);
  const lines = [`CAVADOR · ${safe.name}`];
  const n = value => formatNumber(value, locale);
  for (const { product, calculation } of [...safe.finishes, ...safe.materials]) {
    const name = [product.title, product.variantTitle && product.variantTitle !== 'Default Title' ? product.variantTitle : '', product.sku || product.values.model].filter(Boolean).join(' · ');
    lines.push(name, new URL(product.url, origin).href);
    if (calculation?.valid) lines.push(`${labels.area}: ${n(calculation.areaSqft)} ${labels.sqft} · ${labels.waste}: ${n(calculation.waste)}% · ${product.surface === 'wall' ? labels.packs : labels.boxes}: ${n(calculation.boxes)} · ${labels.total_coverage}: ${n(calculation.totalCoverageSqft)} ${labels.sqft}`);
    else lines.push(calculation ? labels.coverage_pending : labels.measurements_pending);
  }
  if (safe.notes) lines.push(`${labels.notes}:\n${safe.notes}`);
  if (safe.studioLook) lines.push(`${labels.studio_heading}: ${safe.studioLook.roomName}`, studioStatus(safe.studioLook, labels), labels.preview_notice);
  lines.push(labels.installer_notice);
  return lines.join('\n').slice(0, 10000);
}

/** @extends {Component<{dialog: HTMLDialogElement, openButton: HTMLButtonElement, closeButton: HTMLButtonElement, nameInput: HTMLInputElement, list: HTMLDivElement, current: HTMLDivElement, count: HTMLSpanElement, empty: HTMLParagraphElement, saveButton: HTMLButtonElement, addComparedButton: HTMLButtonElement, downloadButton: HTMLButtonElement, storageNotice: HTMLParagraphElement, status: HTMLParagraphElement}>} */
class CavadorFlooringProjects extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.statusTimer);
    if (this.refs.dialog?.open) this.refs.dialog.close();
    for (const url of this.downloadURLs || []) URL.revokeObjectURL(url);
    this.downloadURLs = [];
    super.disconnectedCallback();
  }

  initialize() {
    this.controller?.abort();
    clearTimeout(this.statusTimer);
    try {
      this.labels = JSON.parse(this.querySelector('[data-project-labels]')?.textContent || '{}');
      const record = JSON.parse(this.querySelector('[data-project-product] [data-floor-compare-product]')?.textContent || 'null');
      if (record) {
        record.priceCents = number(Number(this.dataset.priceCents));
        record.currency = this.dataset.currency;
        const facts = this.querySelector('[data-project-product] [data-floor-compare-facts]');
        for (const row of facts?.content?.querySelectorAll('dl > div') || []) {
          const label = row.querySelector('dt')?.textContent.trim();
          for (const field of ['water_resistance', 'underpad']) {
            if (label === this.labels['fact_' + field]) {
              record.values ||= {};
              record.values[field] = cleanText(row.querySelector('dd')?.textContent, 600);
            }
          }
        }
      }
      this.product = normalizeProduct(record);
    } catch { return; }
    if (!this.refs.dialog || typeof this.refs.dialog.showModal !== 'function' || !this.labels.default_name) return;
    this.locale = this.dataset.locale || 'en';
    this.controller = new AbortController();
    this.downloadURLs ||= [];
    this.project = this.readSavedProject();
    const savedCurrent = this.product && this.project.finishes.find(entry => entry.product.handle === this.product.handle);
    if (savedCurrent) {
      savedCurrent.product = this.product;
      savedCurrent.calculation = normalizeCalculation(savedCurrent.calculation, this.product);
      savedCurrent.savedAt = new Date().toISOString();
    }
    this.refs.nameInput.value = this.project.name;
    if (this.refs.notesInput) this.refs.notesInput.value = this.project.notes;
    this.refs.openButton.hidden = false;
    const options = { signal: this.controller.signal };
    this.refs.nameInput.addEventListener('change', () => this.saveName(), options);
    this.refs.notesInput?.addEventListener('change', () => this.saveNotes(), options);
    this.refs.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeProject(); }, options);
    this.refs.dialog.addEventListener('close', () => this.returnFocus?.isConnected && this.returnFocus.focus({ preventScroll: true }), options);
    this.refs.dialog.addEventListener('click', event => {
      if (event.target !== this.refs.dialog) return;
      const rect = this.refs.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeProject();
    }, options);
    this.refs.list.addEventListener('click', event => {
      const button = event.target instanceof Element ? event.target.closest('button[data-project-action]') : null;
      if (!button) return;
      if (button.dataset.projectAction === 'remove') this.removeFinish(button.dataset.handle);
      else if (button.dataset.projectAction === 'load') this.loadMeasurements(button.dataset.handle);
      else if (button.dataset.projectAction === 'remove-material') this.removeMaterial(button.dataset.key);
      else if (button.dataset.projectAction === 'load-material') this.loadMaterial(button.dataset.key);
    }, options);
    this.refs.studioSummary?.addEventListener('click', event => {
      const button = event.target instanceof Element ? event.target.closest('button[data-project-action="remove-studio"]') : null;
      if (button) this.removeStudio();
    }, options);
    document.addEventListener('cavador:flooring-calculation-change', event => {
      if (!this.product || event.detail?.handle !== this.product.handle) return;
      this.renderCurrent();
    }, options);
    document.addEventListener('cavador:material-calculation-change', () => { if (this.refs.dialog.open) this.render(); }, options);
    document.addEventListener('cavador:project-change', event => {
      if (event.detail?.persisted === false) { this.unsavedProject = normalizeProject(event.detail.project, this.labels.default_name); this.storageMessage = this.labels.storage_unavailable; }
      else { delete this.unsavedProject; this.storageMessage = ''; }
      this.project = this.readSavedProject();
      if (document.activeElement !== this.refs.nameInput) this.refs.nameInput.value = this.project.name;
      if (this.refs.notesInput && document.activeElement !== this.refs.notesInput) this.refs.notesInput.value = this.project.notes;
      this.render();
    }, options);
    document.addEventListener('cavador:project-review', () => { if (this.isPrimary()) this.openProject(); }, options);
    document.addEventListener('cavador:material-project', event => { if (this.isPrimary()) this.saveMaterial(event.detail); }, options);
    document.addEventListener('cavador:studio-project', event => { if (this.isPrimary()) this.saveStudio(event.detail); }, options);
    document.addEventListener('cavador:comparison-change', () => this.render(), options);
    window.addEventListener('storage', event => {
      if (event.key !== STORAGE_KEY) return;
      delete this.unsavedProject;
      this.project = this.readSavedProject();
      this.refs.nameInput.value = this.project.name;
      if (this.refs.notesInput) this.refs.notesInput.value = this.project.notes;
      this.render();
    }, options);
    this.render();
    if (this.isPrimary()) {
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', applyCavadorProjectPrefill, { ...options, once: true });
      else applyCavadorProjectPrefill();
    }
  }

  readSavedProject() {
    if (this.unsavedProject) return normalizeProject(this.unsavedProject, this.labels.default_name);
    let raw;
    try { raw = localStorage.getItem(STORAGE_KEY); } catch {
      this.storageMessage = this.labels.storage_unavailable;
      return normalizeProject(null, this.labels.default_name);
    }
    try {
      if (!raw) return normalizeProject(null, this.labels.default_name);
      if (raw.length > MAX_BYTES) throw new Error('oversize');
      const parsed = JSON.parse(raw);
      if (parsed?.version !== 1 || !Array.isArray(parsed.finishes) || parsed.finishes.length > MAX_FINISHES) throw new Error('invalid');
      return normalizeProject(parsed, this.labels.default_name);
    } catch {
      this.storageMessage = this.labels.storage_invalid;
      return normalizeProject(null, this.labels.default_name);
    }
  }

  calculator() {
    if (!this.product) return null;
    return [...document.querySelectorAll('.aurora-calc, [data-flooring-calculator]')].find(root => (root.dataset.flooringHandle || root.dataset.handle) === this.product.handle);
  }

  currentCalculation() {
    if (!this.product) return null;
    return normalizeCalculation(this.calculator()?.cavadorFlooringCalculation, this.product);
  }

  comparedProducts() {
    try {
      const selected = window.CavadorFlooringComparison?.getSelected?.();
      return Array.isArray(selected) ? selected.slice(0, 3).map(product => normalizeProduct({ ...product, currency: product.currency || this.dataset.currency })).filter(Boolean) : [];
    } catch { return []; }
  }

  /** Opens a native modal, which supplies focus containment and keyboard Escape support. */
  openProject() {
    this.project = this.readSavedProject();
    this.refs.nameInput.value = this.project.name;
    if (this.refs.notesInput) this.refs.notesInput.value = this.project.notes;
    this.returnFocus = document.activeElement;
    while (this.returnFocus?.shadowRoot?.activeElement) this.returnFocus = this.returnFocus.shadowRoot.activeElement;
    this.render();
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
    this.refs.closeButton.focus({ preventScroll: true });
  }

  closeProject() {
    if (this.refs.dialog?.open) this.refs.dialog.close();
  }

  saveName(persist = true) {
    const name = cleanText(this.refs.nameInput.value, 80) || this.labels.default_name;
    if (persist) this.project = this.readSavedProject();
    this.project.name = name;
    this.refs.nameInput.value = this.project.name;
    if (persist) this.persist();
  }

  persist(message = this.labels.saved) {
    this.project.updatedAt = new Date().toISOString();
    let saved = false;
    try { saved = persistProject(localStorage, this.project); } catch {}
    if (!saved) { this.storageMessage = this.labels.storage_unavailable; this.unsavedProject = normalizeProject(this.project, this.labels.default_name); }
    else { this.storageMessage = ''; delete this.unsavedProject; }
    document.dispatchEvent(new CustomEvent('cavador:project-change', { detail: saved ? { persisted: true } : { persisted: false, project: this.unsavedProject } }));
    this.render();
    this.announce(saved ? message : this.labels.storage_unavailable);
  }

  saveCurrent() {
    if (!this.product) return;
    this.project = this.readSavedProject();
    if (!this.project.finishes.some(entry => entry.product.handle === this.product.handle) && this.project.finishes.length >= MAX_FINISHES) { this.announce(this.labels.limit); return; }
    this.saveName(false);
    const entry = { product: this.product, calculation: this.currentCalculation(), savedAt: new Date().toISOString() };
    const index = this.project.finishes.findIndex(finish => finish.product.handle === this.product.handle);
    if (index < 0) this.project.finishes.push(entry);
    else this.project.finishes[index] = entry;
    this.persist();
  }

  addCompared() {
    this.project = this.readSavedProject();
    const products = this.comparedProducts();
    const additions = products.filter(product => !this.project.finishes.some(entry => entry.product.handle === product.handle));
    if (this.project.finishes.length + additions.length > MAX_FINISHES) { this.announce(this.labels.limit); return; }
    if (!additions.length) return;
    this.saveName(false);
    for (const product of additions) this.project.finishes.push({ product: product.handle === this.product?.handle ? this.product : product, calculation: product.handle === this.product?.handle ? this.currentCalculation() : null, savedAt: new Date().toISOString() });
    this.persist(this.labels.compared_added);
  }

  removeFinish(handle) {
    this.project = this.readSavedProject();
    const index = this.project.finishes.findIndex(entry => entry.product.handle === handle);
    if (index < 0) return;
    this.project.finishes.splice(index, 1);
    this.persist(this.labels.removed);
    const focus = this.refs.list.querySelector('button') || this.refs.closeButton;
    focus.focus({ preventScroll: true });
  }

  loadMeasurements(handle) {
    if (!this.product || handle !== this.product.handle) return;
    const entry = this.project.finishes.find(finish => finish.product.handle === handle);
    const calculator = this.calculator();
    if (!entry?.calculation || typeof calculator?.restoreCavadorFlooringCalculation !== 'function') { this.announce(this.labels.measurement_unavailable); return; }
    const state = normalizeCalculation(entry.calculation, this.product);
    if (!state || calculator.restoreCavadorFlooringCalculation(state) === false) { this.announce(this.labels.measurement_unavailable); return; }
    this.renderCurrent();
    this.focusProjectAction('load', '', handle);
    this.announce(this.labels.measurements_restored);
  }

  downloadBrief() {
    if (!this.hasContent()) return;
    this.saveName();
    const html = buildProjectBrief(this.project, this.labels, { locale: this.locale });
    let url;
    try {
      url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = (this.project.name.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'cavador-flooring-project') + '.html';
      this.refs.dialog.append(link);
      link.click();
      link.remove();
      this.downloadURLs.push(url);
      // Keep the latest download alive until disconnect; retire older URLs to bound memory.
      if (this.downloadURLs.length > 2) URL.revokeObjectURL(this.downloadURLs.shift());
      this.announce(this.labels.downloaded);
    } catch {
      if (url) URL.revokeObjectURL(url);
      this.announce(this.labels.download_error);
    }
  }

  isPrimary() {
    return (document.querySelector('cavador-flooring-projects[data-global-project="true"]') || document.querySelector('cavador-flooring-projects')) === this;
  }

  hasContent() {
    return Boolean(this.project.finishes.length || this.project.materials.length || this.project.notes || this.project.studioLook);
  }

  saveNotes() {
    const notes = cleanText(this.refs.notesInput?.value, 2500);
    this.project = this.readSavedProject();
    this.project.notes = notes;
    this.persist();
  }

  saveMaterial(detail) {
    const product = normalizeMaterial(detail?.product);
    const calculation = product && normalizeMaterialCalculation(detail?.calculation, product);
    if (!product || !calculation?.valid) { this.announce(this.labels.measurement_unavailable); return; }
    this.project = this.readSavedProject();
    const index = this.project.materials.findIndex(entry => materialKey(entry.product) === materialKey(product));
    if (index < 0 && this.project.materials.length >= MAX_MATERIALS) { this.announce(this.labels.material_limit); return; }
    const entry = { product, calculation, savedAt: new Date().toISOString() };
    if (index < 0) this.project.materials.push(entry);
    else this.project.materials[index] = entry;
    this.persist();
  }

  saveStudio(detail) {
    if (!detail || typeof detail !== 'object') return;
    this.project = this.readSavedProject();
    const floorInput = detail.floor;
    const floor = floorInput && normalizeProduct({ ...floorInput, id: floorInput.productId || floorInput.id, title: floorInput.title || floorInput.name, url: floorInput.url || floorInput.productUrl, image: floorInput.image || floorInput.imageUrl, coveragePerBox: floorInput.coveragePerBox ?? floorInput.coverageSqFt, planksPerBox: floorInput.piecesPerPack });
    const wall = detail.wall && normalizeMaterial({ ...detail.wall, surface: 'wall' });
    const tile = detail.tile && normalizeMaterial({ ...detail.tile, surface: 'tile' });
    const floorIndex = floor ? this.project.finishes.findIndex(entry => entry.product.handle === floor.handle) : -1;
    const additions = [wall, tile].filter(product => product && !this.project.materials.some(entry => materialKey(entry.product) === materialKey(product)));
    if (floor && floorIndex < 0 && this.project.finishes.length >= MAX_FINISHES || this.project.materials.length + additions.length > MAX_MATERIALS) { this.announce(this.labels.material_limit); return; }
    if ((floorInput && !floor) || (detail.wall && !wall) || (detail.tile && !tile) || (floor && tile)) { this.announce(this.labels.material_unavailable); return; }
    const now = new Date().toISOString();
    if (floor && floorIndex < 0) this.project.finishes.push({ product: floor, calculation: null, savedAt: now });
    for (const product of additions) this.project.materials.push({ product, calculation: null, savedAt: now });
    this.project.studioLook = normalizeStudioReference({ ...detail, floor: floor ? { handle: floor.handle, title: floor.title, variantId: floorInput.variantId } : null, tile: tile ? { handle: tile.handle, title: [tile.title, tile.variantTitle].filter(Boolean).join(' · '), variantId: tile.variantId } : null, wall: wall ? { handle: wall.handle, title: wall.title, variantId: wall.variantId } : null, savedAt: now });
    this.persist(this.labels.palette_saved);
  }

  removeMaterial(key) {
    this.project = this.readSavedProject();
    this.project.materials = this.project.materials.filter(entry => materialKey(entry.product) !== key);
    this.persist(this.labels.removed);
    (this.refs.list.querySelector('button') || this.refs.closeButton).focus({ preventScroll: true });
  }

  removeStudio() {
    this.project = this.readSavedProject();
    this.project.studioLook = null;
    this.persist();
    this.refs.closeButton.focus({ preventScroll: true });
  }

  materialCalculator(product) {
    return [...document.querySelectorAll('[data-material-calculator]')].find(root => root.dataset.handle === product.handle && (root.cavadorMaterialCalculation?.surface || root.dataset.surface) === product.surface && String(root.cavadorMaterialCalculation?.variantId || root.dataset.variantId) === product.variantId);
  }

  loadMaterial(key) {
    const entry = this.project.materials.find(entry => materialKey(entry.product) === key);
    const calculator = entry && this.materialCalculator(entry.product);
    if (!entry?.calculation || typeof calculator?.restoreCavadorMaterialCalculation !== 'function' || calculator.restoreCavadorMaterialCalculation(entry.calculation) === false) { this.announce(this.labels.measurement_unavailable); return; }
    this.focusProjectAction('load-material', key);
    this.announce(this.labels.measurements_restored);
  }

  focusProjectAction(action, key = '', handle = '') {
    const button = [...this.refs.list.querySelectorAll('button[data-project-action]')].find(button => button.dataset.projectAction === action && (!key || button.dataset.key === key) && (!handle || button.dataset.handle === handle));
    (button || this.refs.closeButton).focus({ preventScroll: true });
  }

  useContact() { this.useGuide('contact'); }
  useTrade() { this.useGuide('trade'); }

  useGuide(target) {
    if (!this.hasContent()) return;
    this.saveName();
    const entries = [...this.project.finishes, ...this.project.materials];
    const products = entries.map(({ product }) => [product.title, product.variantTitle && product.variantTitle !== 'Default Title' ? product.variantTitle : '', product.sku || product.values.model].filter(Boolean).join(' · ').replace(/\s+/g, ' ')).join('; ').slice(0, 1500);
    const quantities = entries.filter(entry => entry.calculation?.valid).map(({ product, calculation }) => `${product.title}: ${calculation.boxes} ${product.surface === 'wall' ? this.labels.packs : this.labels.boxes}`.replace(/\s+/g, ' ')).join('; ').slice(0, 1500);
    const materials = [...new Set(entries.map(({ product }) => product.surface === 'wall' ? 'panels' : product.surface === 'tile' ? 'tiles' : 'flooring'))];
    const payload = { version: 1, target, createdAt: new Date().toISOString(), name: this.project.name, text: buildProjectText(this.project, this.labels, { locale: this.locale }), products, quantities, materials };
    try { sessionStorage.setItem('cavador-project-prefill:v1', JSON.stringify(payload)); } catch { this.announce(this.labels.storage_unavailable); return; }
    const url = new URL(target === 'contact' ? this.dataset.contactUrl : this.dataset.tradeUrl, window.location.origin);
    if (url.origin !== window.location.origin) return;
    url.searchParams.set('project', 'local');
    window.location.assign(url.href);
  }

  announce(message) {
    clearTimeout(this.statusTimer);
    this.refs.status.textContent = '';
    this.statusTimer = setTimeout(() => { if (this.isConnected) this.refs.status.textContent = message; }, 20);
  }

  summary(product, calculation) {
    if (!calculation) return this.labels.measurements_pending;
    if (!calculation.measurementsComplete) return this.labels.incomplete;
    if (!calculation.valid) return this.labels.coverage_pending;
    const n = value => formatNumber(value, this.locale);
    const estimate = formatMoney(product.priceCents === null ? null : calculation.boxes * product.priceCents, product.currency, this.locale) || this.labels.price_pending;
    return `${this.labels.area}: ${n(calculation.areaSqft)} ${this.labels.sqft} · ${this.labels.waste}: ${n(calculation.waste)}% · ${product.surface === 'wall' ? this.labels.packs : this.labels.boxes}: ${n(calculation.boxes)} · ${estimate}`;
  }

  finishDOM(product, calculation) {
    const finish = document.createElement('div');
    finish.className = 'cfp__finish';
    if (product.image) {
      const image = document.createElement('img');
      image.className = 'cfp__image';
      image.src = product.image;
      image.alt = '';
      image.width = 64;
      image.height = 64;
      image.loading = 'lazy';
      finish.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'cfp__image';
      placeholder.setAttribute('aria-hidden', 'true');
      finish.append(placeholder);
    }
    const body = document.createElement('div');
    const title = document.createElement('a');
    title.className = 'cfp__finish-name';
    title.href = product.url;
    title.textContent = product.title + (product.variantTitle && product.variantTitle !== 'Default Title' ? ` · ${product.variantTitle}` : '');
    body.append(title);
    if (product.sku || product.values.model || product.surface) {
      const model = document.createElement('p');
      model.className = 'cfp__model';
      model.textContent = [product.surface ? this.labels[product.surface] : '', product.sku || product.values.model].filter(Boolean).join(' · ');
      body.append(model);
    }
    const summary = document.createElement('p');
    summary.className = 'cfp__summary';
    summary.textContent = this.summary(product, calculation);
    body.append(summary);
    finish.append(body);
    return finish;
  }

  renderCurrent() {
    this.refs.current.closest('section').hidden = !this.product;
    if (this.product) this.refs.current.replaceChildren(this.finishDOM(this.product, this.currentCalculation()));
  }

  renderComparedButton() {
    this.refs.addComparedButton.hidden = !this.comparedProducts().some(product => !this.project.finishes.some(entry => entry.product.handle === product.handle));
  }

  render() {
    for (const entry of this.project.finishes) {
      let latest = null;
      try { latest = window.CavadorFlooringComparison?.getProduct?.(entry.product.handle); } catch {}
      const product = entry.product.handle === this.product?.handle ? this.product : latest ? normalizeProduct({ ...latest, currency: latest.currency || this.dataset.currency }) : null;
      if (product && JSON.stringify(product) !== JSON.stringify(entry.product)) {
        entry.product = product;
        entry.calculation = normalizeCalculation(entry.calculation, product);
        entry.savedAt = new Date().toISOString();
      }
    }
    this.renderCurrent();
    this.renderComparedButton();
    const count = this.project.finishes.length + this.project.materials.length;
    this.refs.empty.hidden = !!count;
    this.refs.count.hidden = !count;
    this.refs.count.textContent = `(${count})`;
    this.refs.downloadButton.disabled = !this.hasContent();
    for (const button of this.querySelectorAll('[data-project-guide]')) button.disabled = !this.hasContent();
    this.refs.saveButton.textContent = this.project.finishes.some(entry => entry.product.handle === this.product?.handle) ? this.labels.update_current : this.labels.save_current;
    this.refs.storageNotice.textContent = this.storageMessage || this.labels.browser_notice;
    const items = this.project.finishes.map(entry => {
      // Refresh this finish's verified facts when revisiting its page, and derive estimates again.
      const product = entry.product.handle === this.product?.handle ? this.product : entry.product;
      const calculation = normalizeCalculation(entry.calculation, product);
      const item = document.createElement('article');
      item.className = 'cfp__item';
      item.append(this.finishDOM(product, calculation));
      if (calculation) {
        const details = document.createElement('details');
        details.className = 'cfp__rooms';
        const summary = document.createElement('summary');
        summary.textContent = this.labels.rooms;
        const list = document.createElement('dl');
        for (const [i, room] of calculation.rooms.entries()) {
          const row = document.createElement('div');
          const name = document.createElement('dt');
          const value = document.createElement('dd');
          name.textContent = roomName(room, i, this.labels);
          value.textContent = roomValue(room, calculation.units, this.labels, this.locale);
          row.append(name, value);
          list.append(row);
        }
        details.append(summary, list);
        item.append(details);
      }
      const actions = document.createElement('div');
      actions.className = 'cfp__actions';
      if (product.handle === this.product?.handle && calculation) {
        const load = document.createElement('button');
        load.type = 'button';
        load.className = 'cfp__text-button';
        load.dataset.projectAction = 'load';
        load.dataset.handle = product.handle;
        load.textContent = this.labels.load;
        actions.append(load);
      } else {
        const view = document.createElement('a');
        view.href = product.url;
        view.className = 'cfp__link';
        view.textContent = this.labels.view;
        actions.append(view);
      }
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'cfp__text-button';
      remove.dataset.projectAction = 'remove';
      remove.dataset.handle = product.handle;
      remove.textContent = this.labels.remove;
      remove.setAttribute('aria-label', this.labels.remove_label.replace('__TITLE__', product.title));
      actions.append(remove);
      item.append(actions);
      return item;
    });
    const materialItems = this.project.materials.map(({ product, calculation }) => {
      const item = document.createElement('article');
      item.className = 'cfp__item';
      item.append(this.finishDOM(product, calculation));
      if (calculation?.mode === 'dims') {
        const measurements = document.createElement('p');
        measurements.className = 'cfp__summary';
        const unit = calculation.units === 'metric' ? this.labels.m : this.labels.ft;
        measurements.textContent = product.surface === 'wall' ? calculation.walls.map((wall, i) => `${this.labels.wall} ${i + 1}: ${wall.width ?? '—'} × ${wall.height ?? '—'} ${unit}`).join(' · ') : `${calculation.length ?? '—'} × ${calculation.width ?? '—'} ${unit}`;
        item.append(measurements);
      }
      if (product.surface === 'wall' && calculation?.mode === 'dims') {
        const note = document.createElement('p');
        note.className = 'cfp__notice';
        note.textContent = this.labels.wall_layout + (calculation.horizontalJoint ? ' ' + this.labels.wall_joint : '');
        item.append(note);
      }
      const actions = document.createElement('div');
      actions.className = 'cfp__actions';
      if (calculation && this.materialCalculator(product)) {
        const load = document.createElement('button');
        load.type = 'button'; load.className = 'cfp__text-button'; load.dataset.projectAction = 'load-material'; load.dataset.key = materialKey(product); load.textContent = this.labels.load;
        actions.append(load);
      } else {
        const view = document.createElement('a');
        view.href = product.url; view.className = 'cfp__link'; view.textContent = this.labels.view;
        actions.append(view);
      }
      const remove = document.createElement('button');
      remove.type = 'button'; remove.className = 'cfp__text-button'; remove.dataset.projectAction = 'remove-material'; remove.dataset.key = materialKey(product); remove.textContent = this.labels.remove; remove.setAttribute('aria-label', this.labels.remove_label.replace('__TITLE__', product.title));
      actions.append(remove); item.append(actions); return item;
    });
    this.refs.list.replaceChildren(...items, ...materialItems);
    if (this.refs.studioSummary) {
      this.refs.studioSummary.replaceChildren();
      this.refs.studioSummary.closest('section').hidden = !this.project.studioLook;
      if (this.project.studioLook) {
        const reference = this.project.studioLook;
        const title = document.createElement('p'); title.textContent = reference.roomName;
        const selection = document.createElement('p'); selection.textContent = [reference.floor?.title, reference.tile?.title, reference.wall?.title].filter(Boolean).join(' + ');
        const status = document.createElement('p'); status.className = 'cfp__notice'; status.textContent = studioStatus(reference, this.labels);
        const view = document.createElement('a'); view.className = 'cfp__link'; view.href = studioLink(reference, window.location.origin, this.dataset.studioUrl); view.textContent = this.labels.open_studio;
        const actions = document.createElement('div'); actions.className = 'cfp__actions';
        const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'cfp__text-button'; remove.dataset.projectAction = 'remove-studio'; remove.textContent = this.labels.remove; remove.setAttribute('aria-label', this.labels.remove_label.replace('__TITLE__', reference.roomName || this.labels.studio_heading));
        actions.append(view, remove);
        this.refs.studioSummary.append(title, selection, status, actions);
      }
    }
  }
}

function applyCavadorProjectPrefill() {
  const key = 'cavador-project-prefill:v1';
  if (new URL(window.location.href).searchParams.get('project') !== 'local') return false;
  const contact = document.querySelector('.cc__form');
  const trade = document.querySelector('[data-ct-application]');
  const target = contact ? 'contact' : trade ? 'trade' : '';
  const form = contact || trade;
  if (!target || !form || form.dataset.cavadorProjectImported === 'true') return false;
  if (contact?.closest('cavador-contact')?.querySelector('.cc__status--error,.cc__status--success') || contact?.querySelector('.cc__status--error,.cc__status--success') || trade?.querySelector('[data-ct-form-feedback]')) return false;
  let payload;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw || raw.length > 20000) return false;
    payload = JSON.parse(raw);
  } catch { return false; }
  const created = Date.parse(payload?.createdAt);
  if (payload?.version !== 1 || payload.target !== target || !Number.isFinite(created) || created > Date.now() + 60000 || Date.now() - created > 86400000) return false;
  if (typeof payload.text !== 'string' || !payload.text.trim() || payload.text.length > 10000 || typeof payload.products !== 'string' || payload.products.length > 1500 || typeof payload.quantities !== 'string' || payload.quantities.length > 1500) return false;
  const setEmpty = (selector, value) => {
    const field = form.querySelector(selector);
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) || field.value.trim() || !value) return;
    field.value = value;
    field.dispatchEvent(new Event('input', { bubbles: true }));
  };
  if (target === 'contact') {
    setEmpty('[name="contact[product]"]', payload.products);
    setEmpty('[name="contact[quantity]"]', payload.quantities);
    const chosen = form.querySelector('input[name^="contact[material_"]:checked');
    if (!chosen && Array.isArray(payload.materials)) {
      for (const material of payload.materials.filter(value => ['flooring', 'panels', 'tiles'].includes(value))) {
        const field = form.querySelector('[name="contact[material_' + material + ']"]');
        if (field instanceof HTMLInputElement) {
          field.checked = true;
          field.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  } else {
    setEmpty('[data-ct-form-plan]', payload.products);
  }
  setEmpty('textarea[name="contact[body]"]', payload.text);
  form.dataset.cavadorProjectImported = 'true';
  try { sessionStorage.removeItem(key); } catch {}
  return true;
}

if (!customElements.get('cavador-flooring-projects')) customElements.define('cavador-flooring-projects', CavadorFlooringProjects);
